import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { SeasonProvider, useSeasonContext } from "@/contexts/SeasonContext";
import logoOyster from "@/assets/logo-oyster.png";

interface HeaderProps {
  alwaysScrolled?: boolean;
}

const HeaderContent = ({ alwaysScrolled = false }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(alwaysScrolled);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isSummer } = useSeasonContext();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    if (alwaysScrolled) return;
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [alwaysScrolled]);

  // Gestion propre du scroll lock pour éviter de casser le sticky au retour
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const btnBgClass = isSummer ? "bg-summer-accent hover:bg-summer-accent-dark" : "bg-ochre hover:bg-ochre-dark";
  const hoverTextClass = isSummer ? "hover:text-summer-accent" : "hover:text-ochre";

  const navLinks = [
    { href: "about", label: "À Propos" },
    { href: "menu", label: "Menu" },
    { href: "gallery", label: "Galerie" },
    { href: "reviews", label: "Avis" },
    { href: "fournisseurs", label: "Nos Partenaires" },
    { href: "contact", label: "Contact" },
  ];

  const getNavHref = (sectionId: string) => (isHomePage ? `#${sectionId}` : `/#${sectionId}`);

  const handleLogoClick = (e: React.MouseEvent) => {
    if (isHomePage) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-white/60 backdrop-blur-sm z-40 transition-opacity duration-300 lg:hidden ${
          isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 border-b ${
          isScrolled || isMobileMenuOpen
            ? "bg-cream/95 backdrop-blur-md shadow-soft py-3 border-border/10"
            : "bg-transparent py-5 border-transparent"
        }`}
      >
        <div className="container-narrow flex items-center justify-between px-4">
          <a
            href={getNavHref("hero")}
            onClick={handleLogoClick}
            className={`flex items-center gap-2 text-xl md:text-3xl font-semibold transition-colors duration-300 shrink-0 ${
              isScrolled || isMobileMenuOpen ? "text-foreground" : "text-white"
            }`}
            style={{ fontFamily: "Cinzel, serif" }}
          >
            <img src={logoOyster} alt="Logo L'Ostrea" className="h-7 md:h-10 w-auto object-contain" />
            <span className="truncate">L'Ostréa</span>
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={getNavHref(link.href)}
                className={`text-sm font-medium transition-all duration-300 ${
                  isScrolled ? `text-foreground/80 ${hoverTextClass}` : "text-white/90 hover:text-sand"
                }`}
              >
                {link.label}
              </a>
            ))}
            <a
              href={getNavHref("contact")}
              className={`inline-flex items-center justify-center px-6 py-2.5 text-white font-medium rounded-lg transition-all duration-300 shadow-soft hover:shadow-medium transform hover:-translate-y-0.5 text-sm ${btnBgClass}`}
            >
              Réservation Obligatoire
            </a>
          </nav>

          <button
            className={`lg:hidden p-2 transition-colors duration-300 ${
              isScrolled || isMobileMenuOpen ? "text-foreground" : "text-white"
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        <div
          className={`lg:hidden absolute top-full left-0 w-full bg-cream/98 border-t border-border/50 shadow-xl transition-all duration-300 ease-in-out origin-top ${
            isMobileMenuOpen ? "opacity-100 scale-y-100 visible" : "opacity-0 scale-y-95 invisible"
          }`}
        >
          <nav className="flex flex-col py-6 px-6 gap-4 max-h-[80vh] overflow-y-auto">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={getNavHref(link.href)}
                className={`text-lg font-medium text-foreground/80 ${hoverTextClass} transition-colors py-3 border-b border-border/40`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href={getNavHref("contact")}
              className={`text-center mt-4 py-4 text-lg text-white font-medium rounded-lg shadow-lg ${btnBgClass}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Réservation Obligatoire
            </a>
          </nav>
        </div>
      </header>
    </>
  );
};

const Header = (props: HeaderProps) => (
  <SeasonProvider>
    <HeaderContent {...props} />
  </SeasonProvider>
);

export default Header;
